// =============================================================================
// LABELLISATION TRACKER
// Bloc interactif de suivi de labellisation qualité (auto-écoles)
// =============================================================================

const LabellisationTracker = {

  // ── État interne ────────────────────────────────────────────────────────────
  // steps      : définition statique des 32 sous-critères (injectée par PHP)
  // progression: dictionnaire { step_id → { checklist, files, completed } }
  // currentIndex : slide actuellement visible (0-based)
  // isSaving   : verrou pour éviter les doubles soumissions
  state: {
    steps: [],
    progression: {},
    currentIndex: 0,
    totalSteps: 0,
    isSaving: false,
    isReviewing: false,
  },

  // Références DOM mises en cache à l'init pour éviter les re-queries répétées
  el: {
    container: null,
    progressFill: null,
    progressBar: null,
    progressCount: null,
    critereLabel: null,
    carousel: null,
    slides: [],
    navPrev: null,
    navNext: null,
    navCounter: null,
    nav: null,
    completion: null,
    completionDocuments: null,
    sidebarNav: null,
  },

  // ── Point d'entrée ──────────────────────────────────────────────────────────
  async init() {
    this.el.container = document.getElementById('labellisation-tracker');
    if (!this.el.container) return;
    if (typeof labellisationData === 'undefined') return;

    // 1. Lire la définition statique des étapes injectée par PHP dans
    //    <script id="labellisation-steps-config" type="application/json">
    const configEl = document.getElementById('labellisation-steps-config');
    if (configEl) {
      try {
        this.state.steps      = JSON.parse(configEl.textContent);
        this.state.totalSteps = this.state.steps.length;
      } catch (e) {
        console.error('[LabellisationTracker] Impossible de parser les étapes', e);
        return;
      }
    }

    // 2. Mettre en cache tous les éléments DOM utiles
    this.el.progressFill  = this.el.container.querySelector('.labellisation-tracker__progress-bar-fill');
    this.el.progressBar   = this.el.container.querySelector('.labellisation-tracker__progress-bar-track');
    this.el.progressCount = this.el.container.querySelector('.labellisation-tracker__progress-count');
    this.el.critereLabel  = this.el.container.querySelector('.labellisation-tracker__critere-label-inner');
    this.el.carousel      = this.el.container.querySelector('.labellisation-tracker__carousel');
    this.el.slides        = [...this.el.container.querySelectorAll('.labellisation-tracker__slide')];
    this.el.navPrev             = this.el.container.querySelector('.labellisation-tracker__nav-prev-button');
    this.el.navNext             = this.el.container.querySelector('.labellisation-tracker__nav-next-button');
    this.el.navCounter          = this.el.container.querySelector('.labellisation-tracker__nav-counter-current');
    this.el.nav                 = this.el.container.querySelector('.labellisation-tracker__nav');
    this.el.completion          = document.getElementById('labellisation-completion');
    this.el.completionDocuments = document.getElementById('labellisation-completion-documents');
    this.el.sidebarNav          = this.el.container.querySelector('.labellisation-tracker-sidebar-navigation');

    // 3. Attacher les listeners AVANT le chargement API pour que l'UI
    //    soit réactive dès que les données arrivent
    this.bindEvents();
    this.bindCompletionEvents();

    // 4. Charger la progression sauvegardée depuis le REST endpoint GET
    await this.loadProgression();

    // 5. Appliquer la progression sur les slides (cocher les cases, lister les fichiers…)
    this.applyProgression();

    // 6. Si toutes les étapes sont déjà complétées, afficher directement l'écran de félicitations
    if (this.isAllCompleted()) {
      this.showCompletionScreen();
      return;
    }

    // 7. Afficher l'étape courante (sans animation au démarrage)
    this.showStep(this.state.currentIndex, false);
  },

  // ── Chargement de la progression via REST API ───────────────────────────────
  // GET /wp-json/labellisation/v1/get-progress
  // Retourne : { steps: { step_id: {...} }, current_step: int }
  async loadProgression() {
    if (!labellisationData.isLoggedIn) return;

    try {
      const response = await fetch(`${labellisationData.restUrl}/get-progress`, {
        method: 'GET',
        headers: {
          'X-WP-Nonce':   labellisationData.nonce,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      // Le backend retourne soit un objet indexé, soit un tableau — on normalise
      const raw = data.steps || {};
      if (Array.isArray(raw)) {
        raw.forEach((step) => { this.state.progression[step.step_id] = step; });
      } else {
        this.state.progression = raw;
      }

      this.state.currentIndex = Math.min(
        parseInt(data.current_step, 10) || 0,
        this.state.totalSteps - 1
      );
    } catch (err) {
      console.warn('[LabellisationTracker] Impossible de charger la progression :', err);
    }
  },

  // ── Application de la progression sur les slides ───────────────────────────
  // Pour chaque slide, on restaure l'état sauvegardé :
  //   - cases cochées
  //   - fichiers listés
  //   - classe --completed si l'étape est terminée
  applyProgression() {
    this.el.slides.forEach((slide) => {
      const stepId   = slide.dataset.stepId;
      const stepData = this.state.progression[stepId];

      if (stepData) {
        // Cocher les cases
        const checkboxes = slide.querySelectorAll('.labellisation-tracker__checklist-checkbox');
        checkboxes.forEach((cb, i) => {
          cb.checked = !!(stepData.checklist && stepData.checklist[i]);
        });

        // Restaurer les fichiers
        if (stepData.files?.length) {
          stepData.files.forEach((url) => this.addFileToList(slide, url, this.fileNameFromUrl(url)));
        }

        // Marquer l'étape comme terminée visuellement
        if (stepData.completed) {
          slide.classList.add('labellisation-tracker__slide--completed');
        }
      }

      // Mettre à jour l'état du bouton "Valider" dans tous les cas
      this.updateValidateButton(slide);

      // Griser les champs si "Non concerné" était déjà coché
      this.syncNonConcerneState(slide);
    });

    this.updateProgressBar();

    // Synchroniser les états complétés dans la sidebar
    this.state.steps.forEach((step) => {
      if (this.state.progression[step.id]?.completed) {
        this.updateSidebarCompletionState(step.id);
      }
    });
  },

  // ── Affichage d'une slide ───────────────────────────────────────────────────
  // animate=true : animation CSS (glissement gauche/droite)
  // animate=false : affichage immédiat (init / navigation rapide)
  showStep(index, animate = true) {
    const previous = this.el.slides[this.state.currentIndex];
    const target   = this.el.slides[index];
    if (!target) return;

    // Masquer la slide précédente
    if (previous && previous !== target) {
      previous.classList.remove('labellisation-tracker__slide--active');
      previous.setAttribute('aria-hidden', 'true');
    }

    // Ajouter la classe d'animation si demandé
    // --enter-right : on avance (index croissant)
    // --enter-left  : on recule (index décroissant)
    if (animate && previous !== target) {
      const dir = index > this.state.currentIndex ? 'enter-right' : 'enter-left';
      target.classList.add(`labellisation-tracker__slide--${dir}`);
      void target.offsetWidth; // force reflow pour déclencher la transition CSS
      target.classList.remove(`labellisation-tracker__slide--${dir}`);
    }

    target.classList.add('labellisation-tracker__slide--active');
    target.setAttribute('aria-hidden', 'false');
    this.state.currentIndex = index;

    // Synchroniser la sidebar
    this.updateSidebarActiveState(index);

    // Mettre à jour le label du critère courant
    if (this.el.critereLabel) {
      this.el.critereLabel.textContent = target.dataset.critereLabel || '';
    }

    // Mettre à jour le compteur "Étape X / 32"
    if (this.el.navCounter) this.el.navCounter.textContent = index + 1;

    // Afficher/masquer le bouton "Étape précédente" de la slide active
    const prevBtn = target.querySelector('.labellisation-tracker__btn-prev');
    if (prevBtn) {
      if (index === 0) {
        prevBtn.setAttribute('hidden', '');
      } else {
        prevBtn.removeAttribute('hidden');
      }
    }

    // En mode révision : adapter les boutons de la slide active
    if (this.state.isReviewing) {
      const btnNext  = target.querySelector('.labellisation-tracker__btn-next');
      const btnRecap = target.querySelector('.labellisation-tracker__btn-recap');
      const isLast   = index === this.state.totalSteps - 1;

      if (btnRecap) btnRecap.removeAttribute('hidden');

      if (btnNext) {
        if (isLast) {
          btnNext.setAttribute('hidden', '');
        } else {
          btnNext.removeAttribute('hidden');
          btnNext.disabled = false;
          btnNext.removeAttribute('aria-disabled');
          btnNext.querySelector('span, :first-child') ?? null;
          // Changer le libellé
          const textNode = [...btnNext.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
          if (textNode) textNode.textContent = 'Étape suivante';
        }
      }
    }

    // Scroll pour ramener le haut du bloc en haut du viewport (sous le header fixe)
    if (animate) {
      const top = this.el.container.getBoundingClientRect().top + window.scrollY - 32;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  },

  // ── Barre de progression globale ───────────────────────────────────────────
  // Compte les étapes avec completed:true et calcule le pourcentage
  updateProgressBar() {
    const completed = this.state.steps.filter((step) => this.state.progression[step.id]?.completed === true).length;
    const pct       = this.state.totalSteps > 0
      ? Math.round((completed / this.state.totalSteps) * 100)
      : 0;

    if (this.el.progressFill)  this.el.progressFill.style.width = `${pct}%`;
    if (this.el.progressBar)   this.el.progressBar.setAttribute('aria-valuenow', completed);
    if (this.el.progressCount) this.el.progressCount.textContent = completed;
  },

  // ── Règles de validation du bouton "Valider et continuer" ──────────────────
  // Le bouton n'est actif que si :
  //   1. TOUTES les cases de la checklist sont cochées
  //   2. Si un upload est requis → au moins un fichier dans la liste
  canValidate(slide) {
    const stepId  = slide.dataset.stepId;
    const stepDef = this.state.steps.find((s) => s.id === stepId);

    const checkboxes = [...slide.querySelectorAll('.labellisation-tracker__checklist-checkbox')];
    const anyMode    = stepDef?.checklistMode === 'any';
    const allChecked = checkboxes.length > 0 && (
      anyMode ? checkboxes.some((cb) => cb.checked) : checkboxes.every((cb) => cb.checked)
    );

    let uploadOk = true;
    if (stepDef?.upload) {
      // En mode 'any', si la première case ("Non concerné") est cochée, pas d'upload requis
      const nonConcerneChecked = anyMode && checkboxes[0]?.checked;
      if (!nonConcerneChecked) {
        const fileList = slide.querySelector('.labellisation-tracker__upload-file-list');
        uploadOk = fileList ? fileList.childElementCount > 0 : false;
      }
    }

    return allChecked && uploadOk;
  },

  updateValidateButton(slide) {
    const btn = slide.querySelector('.labellisation-tracker__btn-next');
    if (!btn) return;
    const valid  = this.canValidate(slide);
    btn.disabled = !valid;
    btn.setAttribute('aria-disabled', String(!valid));

    // Libellé "Valider et terminer" si c'est la dernière étape à compléter
    const stepId          = slide.dataset.stepId;
    const isLastRemaining = valid && this.state.steps
      .filter((s) => s.id !== stepId)
      .every((s) => this.state.progression[s.id]?.completed === true);

    const textNode = [...btn.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
    if (textNode) textNode.textContent = isLastRemaining ? 'Valider et terminer ' : 'Valider et continuer ';
  },

  // ── Validation et sauvegarde d'une étape ───────────────────────────────────
  // POST /wp-json/labellisation/v1/save-step
  // Après succès : marque --completed, met à jour la barre, avance d'une slide
  async validateStep(slide) {
    if (this.state.isSaving) return;

    const stepId    = slide.dataset.stepId;
    const stepIndex = parseInt(slide.dataset.stepIndex, 10);

    const checklist = [...slide.querySelectorAll('.labellisation-tracker__checklist-checkbox')]
      .map((cb) => cb.checked);

    const files = [...slide.querySelectorAll('[data-file-url]')]
      .map((item) => item.dataset.fileUrl);

    const nextIndex = Math.min(stepIndex + 1, this.state.totalSteps - 1);
    const btn       = slide.querySelector('.labellisation-tracker__btn-next');

    // Feedback visuel de chargement
    if (btn) {
      btn.disabled = true;
      btn.classList.add('labellisation-tracker__btn-next--saving');
    }
    this.state.isSaving = true;

    try {
      const response = await fetch(`${labellisationData.restUrl}/save-step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce':   labellisationData.nonce,
        },
        body: JSON.stringify({ step_id: stepId, checklist, files, completed: true, current_step: nextIndex }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Mise à jour de l'état local (pas besoin de re-fetcher)
      this.state.progression[stepId] = { step_id: stepId, checklist, files, completed: true };
      slide.classList.add('labellisation-tracker__slide--completed');
      this.updateSidebarCompletionState(stepId);
      this.updateProgressBar();

      // Vérifier la complétion totale en priorité, peu importe l'ordre de validation
      if (this.isAllCompleted()) {
        this.showCompletionScreen();
        return;
      }

      // Sinon, avancer à l'étape suivante si elle existe
      if (nextIndex !== stepIndex) {
        this.showStep(nextIndex);
      }
    } catch (err) {
      console.error('[LabellisationTracker] Erreur de sauvegarde :', err);
      // En cas d'erreur, réactiver le bouton pour permettre une nouvelle tentative
      if (btn) btn.disabled = false;
    } finally {
      this.state.isSaving = false;
      if (btn) btn.classList.remove('labellisation-tracker__btn-next--saving');
    }
  },

  // ── Upload d'un fichier ─────────────────────────────────────────────────────
  // wp_ajax_labellisation_upload_file (PHP)
  // Envoie le fichier via FormData, affiche le résultat dans la liste
  async uploadFile(slide, file) {
    const uploadZone = slide.querySelector('.labellisation-tracker__upload-zone');
    if (uploadZone) uploadZone.classList.add('labellisation-tracker__upload-zone--uploading');

    const formData = new FormData();
    formData.append('action',              'labellisation_upload_file');
    formData.append('nonce',               labellisationData.uploadNonce);
    formData.append('labellisation_file',  file);

    try {
      const response = await fetch(labellisationData.ajaxUrl, { method: 'POST', body: formData });
      const data     = await response.json();

      if (data.success) {
        this.addFileToList(slide, data.data.url, data.data.name);
        this.updateValidateButton(slide);
      } else {
        this.showUploadError(slide, data.data?.message || 'Erreur lors de l\'upload.');
      }
    } catch (err) {
      this.showUploadError(slide, 'Erreur réseau, veuillez réessayer.');
      console.error('[LabellisationTracker] Erreur upload :', err);
    } finally {
      // Réinitialiser l'input pour permettre le re-upload du même fichier
      const input = slide.querySelector('.labellisation-tracker__upload-input');
      if (input) input.value = '';
      if (uploadZone) uploadZone.classList.remove('labellisation-tracker__upload-zone--uploading');
    }
  },

  // Ajoute une ligne de fichier dans la liste d'une slide
  // Chaque ligne contient : icône, nom cliquable, bouton de suppression
  addFileToList(slide, fileUrl, fileName) {
    const list = slide.querySelector('.labellisation-tracker__upload-file-list');
    if (!list) return;

    const name = fileName || this.fileNameFromUrl(fileUrl);
    const item = document.createElement('li');
    item.className       = 'labellisation-tracker__upload-file';
    item.dataset.fileUrl = fileUrl;
    item.innerHTML = `
      <svg class="labellisation-tracker__upload-file-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      <a href="${this.escapeHtml(fileUrl)}" target="_blank" rel="noopener" class="labellisation-tracker__upload-file-name">${this.escapeHtml(name)}</a>
      <button type="button" class="labellisation-tracker__upload-file-remove" aria-label="Supprimer ${this.escapeHtml(name)}">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>`;

    item.querySelector('.labellisation-tracker__upload-file-remove').addEventListener('click', () => {
      item.remove();
      this.updateValidateButton(slide);
      this.updateUploadButton(slide);
      this.syncStepCompletionVisual(slide);
    });

    list.appendChild(item);
    this.updateUploadButton(slide);
  },

  // Affiche un message d'erreur sous la zone d'upload, disparaît après 5s
  showUploadError(slide, message) {
    const zone = slide.querySelector('.labellisation-tracker__upload-zone');
    if (!zone) return;
    const existing = zone.querySelector('.labellisation-tracker__upload-error');
    if (existing) existing.remove();
    const err       = document.createElement('p');
    err.className   = 'labellisation-tracker__upload-error';
    err.textContent = message;
    zone.appendChild(err);
    setTimeout(() => err.remove(), 5000);
  },

  // ── Écran de félicitations ──────────────────────────────────────────────────

  // Vérifie si chaque étape définie a un enregistrement completed:true
  isAllCompleted() {
    if (this.state.totalSteps === 0) return false;
    return this.state.steps.every((step) => this.state.progression[step.id]?.completed === true);
  },

  showCompletionScreen() {
    this.state.isReviewing = false;
    if (this.el.carousel)    this.el.carousel.hidden = true;
    if (this.el.nav)         this.el.nav.hidden = true;
    if (this.el.completion)  this.el.completion.hidden = false;
    if (this.el.critereLabel) this.el.critereLabel.textContent = 'Félicitations !';
    this.buildDocumentList();
  },

  hideCompletionScreen() {
    this.state.isReviewing = true;
    if (this.el.completion) this.el.completion.hidden = true;
    if (this.el.carousel)   this.el.carousel.hidden = false;
    if (this.el.nav)        this.el.nav.hidden = false;
    this.showStep(this.state.currentIndex, true);
  },

  // Construit la liste des documents groupés par critère dans l'écran de félicitations
  buildDocumentList() {
    if (!this.el.completionDocuments) return;

    const critereConfigEl = document.getElementById('labellisation-critere-labels');
    let critereLabels = {};
    if (critereConfigEl) {
      try { critereLabels = JSON.parse(critereConfigEl.textContent); } catch {}
    }

    // Grouper les étapes ayant des fichiers par critère
    const grouped = {};
    this.state.steps.forEach((step) => {
      const prog = this.state.progression[step.id];
      if (prog?.files?.length) {
        const c = step.critere;
        if (!grouped[c]) grouped[c] = [];
        grouped[c].push({ step, files: prog.files });
      }
    });

    const critereKeys = Object.keys(grouped).map(Number).sort((a, b) => a - b);

    if (critereKeys.length === 0) {

      this.el.completionDocuments.innerHTML =
        '<p class="labellisation-tracker__completion-no-docs">Aucun document n\'a été déposé.</p>';
      return;
    }

    const downloadIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>`;
    const chevronIconSvg  = `<svg class="labellisation-tracker__completion-group-chevron" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>`;
    const arrowIconSvg    = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>`;

    let html = '';
    critereKeys.forEach((critereNum) => {
      const label      = critereLabels[critereNum] || '';
      const groupTitle = critereNum > 0 ? `Critère ${critereNum} — ${label}` : label;
      html += `<div class="labellisation-tracker__completion-group">`;
      html += `<div class="labellisation-tracker__completion-group-header">`;
      html += `<button type="button" class="labellisation-tracker__completion-group-toggle" aria-expanded="false">`;
      html += `${this.escapeHtml(groupTitle)}${chevronIconSvg}`;
      html += `</button>`;
      html += `</div>`;
      html += `<div class="labellisation-tracker__completion-group-content">`;
      html += `<ul class="labellisation-tracker__completion-group-list">`;

      grouped[critereNum].forEach(({ step, files }) => {
        const stepIndex = this.state.steps.findIndex((s) => s.id === step.id);
        html += `<li class="labellisation-tracker__completion-group-item">`;
        html += `<div class="labellisation-tracker__completion-item-header">`;
        html += `<span class="labellisation-tracker__completion-item-step-label">${this.escapeHtml(step.id)} — ${this.escapeHtml(step.label)}</span>`;
        html += `<button type="button" class="labellisation-tracker__completion-goto-button" data-goto-step="${stepIndex}">Revoir ${arrowIconSvg}</button>`;
        html += `</div>`;
        html += `<ul class="labellisation-tracker__completion-item-files">`;
        files.forEach((url) => {
          html += `<li><a href="${this.escapeHtml(url)}" target="_blank" rel="noopener" class="labellisation-tracker__completion-file-link">${downloadIconSvg}Télécharger le document</a></li>`;
        });
        html += `</ul></li>`;
      });

      html += `</ul></div></div>`;
    });

    this.el.completionDocuments.innerHTML = html;
  },

  // ── Téléchargement ZIP de tous les documents ───────────────────────────────
  async downloadAllAsZip() {
    const btn = document.getElementById('labellisation-download-zip-button');
    if (btn) {
      btn.disabled = true;
      btn.classList.add('labellisation-tracker__completion-download-button--loading');
    }

    try {
      const response = await fetch(`${labellisationData.restUrl}/download-zip`, {
        method: 'GET',
        headers: { 'X-WP-Nonce': labellisationData.nonce },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `labellisation-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[LabellisationTracker] Erreur téléchargement ZIP :', err);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('labellisation-tracker__completion-download-button--loading');
      }
    }
  },

  bindCompletionEvents() {
    document.getElementById('labellisation-review-button')
      ?.addEventListener('click', () => this.hideCompletionScreen());

    document.getElementById('labellisation-download-zip-button')
      ?.addEventListener('click', () => this.downloadAllAsZip());

    // Délégation pour les accordéons et boutons "Revoir le critère"
    this.el.completionDocuments?.addEventListener('click', (e) => {
      // Bouton "Revoir" → navigation directe vers le sous-critère
      const gotoBtn = e.target.closest('.labellisation-tracker__completion-goto-button');
      if (gotoBtn) {
        const stepIndex = Number(gotoBtn.dataset.gotoStep);
        this.hideCompletionScreen();
        if (!isNaN(stepIndex) && stepIndex >= 0) this.showStep(stepIndex);
        return;
      }

      const toggle = e.target.closest('.labellisation-tracker__completion-group-toggle');
      if (!toggle) return;
      const group    = toggle.closest('.labellisation-tracker__completion-group');
      const isOpen   = group.classList.contains('labellisation-tracker__completion-group--open');
      group.classList.toggle('labellisation-tracker__completion-group--open', !isOpen);
      toggle.setAttribute('aria-expanded', String(!isOpen));
    });
  },

  // ── Liaison des événements (délégation sur le carousel) ────────────────────
  // Toute la gestion passe par deux listeners sur le carousel plutôt que
  // N listeners sur chaque élément individuel → meilleure performance
  bindEvents() {
    if (!this.el.carousel) return;

    // Délégation de clicks sur le carousel
    this.el.carousel.addEventListener('click', (e) => {
      // "Valider et continuer" / "Étape suivante" (mode révision)
      const btnNext = e.target.closest('.labellisation-tracker__btn-next');
      if (btnNext && !btnNext.disabled) {
        const slide = btnNext.closest('.labellisation-tracker__slide');
        if (slide) {
          if (this.state.isReviewing) {
            const stepIndex = parseInt(slide.dataset.stepIndex, 10);
            const nextIndex = Math.min(stepIndex + 1, this.state.totalSteps - 1);
            if (nextIndex !== stepIndex) this.showStep(nextIndex);
          } else {
            this.validateStep(slide);
          }
        }
      }

      // "Voir le récapitulatif" (mode révision)
      const btnRecap = e.target.closest('.labellisation-tracker__btn-recap');
      if (btnRecap) {
        this.showCompletionScreen();
      }

      // "Étape précédente"
      const btnPrev = e.target.closest('.labellisation-tracker__btn-prev');
      if (btnPrev && this.state.currentIndex > 0) {
        this.showStep(this.state.currentIndex - 1);
      }

      // Accordéon documents / solutions
      const accordionToggle = e.target.closest('.labellisation-tracker__step-accordion-toggle');
      if (accordionToggle) {
        const wrapper = accordionToggle.closest('.labellisation-tracker__step-resources, .labellisation-tracker__step-solutions');
        const isOpen  = wrapper?.classList.contains('labellisation-tracker__step-accordion--open');
        wrapper?.classList.toggle('labellisation-tracker__step-accordion--open', !isOpen);
        accordionToggle.setAttribute('aria-expanded', String(!isOpen));
      }

      // "Ajouter un document" → déclenche le file picker natif
      const btnUpload = e.target.closest('.labellisation-tracker__upload-button');
      if (btnUpload) {
        const slide = btnUpload.closest('.labellisation-tracker__slide');
        slide?.querySelector('.labellisation-tracker__upload-input')?.click();
      }
    });

    // Délégation de changements (checkboxes + input file)
    this.el.carousel.addEventListener('change', (e) => {
      // Case à cocher de la checklist
      if (e.target.classList.contains('labellisation-tracker__checklist-checkbox')) {
        const slide = e.target.closest('.labellisation-tracker__slide');
        if (slide) {
          this.syncNonConcerneState(slide);
          this.updateValidateButton(slide);
          this.syncStepCompletionVisual(slide);
        }
      }

      // Sélection d'un ou plusieurs fichiers à uploader
      if (e.target.classList.contains('labellisation-tracker__upload-input')) {
        const slide = e.target.closest('.labellisation-tracker__slide');
        if (!slide) return;
        const files = [...(e.target.files || [])];
        if (!files.length) return;
        const maxFiles     = this.getMaxFiles(slide);
        const fileList     = slide.querySelector('.labellisation-tracker__upload-file-list');
        const currentCount = fileList?.childElementCount || 0;
        const remaining    = maxFiles - currentCount;
        if (remaining <= 0) return;
        const maxBytes = 5 * 1024 * 1024;
        files.slice(0, remaining).forEach((file) => {
          if (file.size > maxBytes) {
            this.showUploadError(slide, `"${file.name}" dépasse la taille maximale autorisée (5 Mo).`);
            return;
          }
          this.uploadFile(slide, file);
        });
      }
    });

    // Délégation de clicks sur la sidebar
    if (this.el.sidebarNav) {
      this.el.sidebarNav.addEventListener('click', (e) => {
        // Accordéon de groupe : déplier le groupe cliqué, replier tous les autres
        const groupHeader = e.target.closest('.labellisation-tracker-sidebar-navigation__group-header');
        if (groupHeader) {
          const group = groupHeader.closest('.labellisation-tracker-sidebar-navigation__group');
          const isExpanded = group.classList.contains('labellisation-tracker-sidebar-navigation__group--expanded');

          // Replier tous les groupes
          this.el.sidebarNav.querySelectorAll('.labellisation-tracker-sidebar-navigation__group').forEach((g) => {
            g.classList.remove('labellisation-tracker-sidebar-navigation__group--expanded');
            const h = g.querySelector('.labellisation-tracker-sidebar-navigation__group-header');
            if (h) h.setAttribute('aria-expanded', 'false');
          });

          // Si le groupe n'était pas ouvert, le déplier
          if (!isExpanded) {
            group.classList.add('labellisation-tracker-sidebar-navigation__group--expanded');
            groupHeader.setAttribute('aria-expanded', 'true');
          }
          return;
        }

        // Bouton d'étape : naviguer directement vers ce slide
        const stepBtn = e.target.closest('.labellisation-tracker-sidebar-navigation__step-button');
        if (stepBtn) {
          const goto = parseInt(stepBtn.dataset.goto, 10);
          if (!isNaN(goto)) {
            if (this.el.completion && !this.el.completion.hidden) {
              this.hideCompletionScreen();
            }
            // Pas de scroll depuis la sidebar : l'utilisateur voit déjà le tracker
            this.showStep(goto, false);
          }
        }
      });
    }

    // Navigation clavier gauche/droite quand le focus est dans le bloc
    document.addEventListener('keydown', (e) => {
      if (!this.el.container?.contains(document.activeElement) &&
          document.activeElement !== document.body) return;
      if (e.key === 'ArrowLeft'  && this.state.currentIndex > 0) this.showStep(this.state.currentIndex - 1);
      if (e.key === 'ArrowRight' && this.state.currentIndex < this.state.totalSteps - 1) this.showStep(this.state.currentIndex + 1);
    });
  },

  // ── Sidebar : mise à jour de l'état actif ──────────────────────────────────
  // Met en surbrillance le bouton de l'étape courante et déplie son groupe.
  // Tous les autres groupes sont repliés.
  updateSidebarActiveState(index) {
    if (!this.el.sidebarNav) return;

    const step = this.state.steps[index];
    if (!step) return;

    // Mettre à jour l'état actif de chaque bouton d'étape
    const allButtons = this.el.sidebarNav.querySelectorAll('.labellisation-tracker-sidebar-navigation__step-button');
    allButtons.forEach((btn) => {
      const isActive = parseInt(btn.dataset.goto, 10) === index;
      btn.classList.toggle('labellisation-tracker-sidebar-navigation__step-button--active', isActive);
      btn.setAttribute('aria-current', isActive ? 'step' : 'false');
    });

    // Déplier le groupe du critère courant, replier les autres
    const groups = this.el.sidebarNav.querySelectorAll('.labellisation-tracker-sidebar-navigation__group');
    groups.forEach((group) => {
      const isCurrentCritere = parseInt(group.dataset.critere, 10) === step.critere;
      group.classList.toggle('labellisation-tracker-sidebar-navigation__group--expanded', isCurrentCritere);
      const header = group.querySelector('.labellisation-tracker-sidebar-navigation__group-header');
      if (header) header.setAttribute('aria-expanded', String(isCurrentCritere));
    });
  },

  // ── Grisage des champs quand "Non concerné" est coché ─────────────────────
  // Appelé à chaque changement de checkbox et à la restauration de progression.
  // Ne fait rien si l'étape n'est pas en mode 'any'.
  syncNonConcerneState(slide) {
    const stepId  = slide.dataset.stepId;
    const stepDef = this.state.steps.find((s) => s.id === stepId);
    if (stepDef?.checklistMode !== 'any') return;

    const checkboxes = [...slide.querySelectorAll('.labellisation-tracker__checklist-checkbox')];
    if (checkboxes.length < 2) return;

    const isNonConcerne = checkboxes[0]?.checked === true;

    checkboxes.slice(1).forEach((cb) => {
      const item = cb.closest('.labellisation-tracker__checklist-item');
      cb.disabled = isNonConcerne;
      item?.classList.toggle('labellisation-tracker__checklist-item--disabled', isNonConcerne);
    });

    const upload = slide.querySelector('.labellisation-tracker__upload');
    if (upload) {
      upload.classList.toggle('labellisation-tracker__upload--disabled', isNonConcerne);
      const input = upload.querySelector('.labellisation-tracker__upload-input');
      const btn   = upload.querySelector('.labellisation-tracker__upload-button');
      if (input) input.disabled = isNonConcerne;
      if (btn)   btn.disabled   = isNonConcerne;
    }
  },

  // ── Retrait visuel de la coche si une étape validée redevient incomplète ────
  // Appelé à chaque modification de checklist ou suppression de fichier.
  // Ne touche pas this.state.progression (pas de sauvegarde API).
  syncStepCompletionVisual(slide) {
    if (!slide.classList.contains('labellisation-tracker__slide--completed')) return;
    if (this.canValidate(slide)) return;

    const stepId = slide.dataset.stepId;
    slide.classList.remove('labellisation-tracker__slide--completed');

    if (this.el.sidebarNav) {
      const btn = this.el.sidebarNav.querySelector(
        `.labellisation-tracker-sidebar-navigation__step-button[data-step-id="${stepId}"]`
      );
      if (btn) btn.classList.remove('labellisation-tracker-sidebar-navigation__step-button--completed');
    }
  },

  // ── Sidebar : marquer une étape comme complétée ─────────────────────────────
  updateSidebarCompletionState(stepId) {
    if (!this.el.sidebarNav) return;
    const btn = this.el.sidebarNav.querySelector(
      `.labellisation-tracker-sidebar-navigation__step-button[data-step-id="${stepId}"]`
    );
    if (btn) btn.classList.add('labellisation-tracker-sidebar-navigation__step-button--completed');
  },

  // ── Utilitaires ────────────────────────────────────────────────────────────

  getMaxFiles(slide) {
    const upload = slide.querySelector('.labellisation-tracker__upload');
    return parseInt(upload?.dataset.maxFiles || '1', 10);
  },

  updateUploadButton(slide) {
    const btn = slide.querySelector('.labellisation-tracker__upload-button');
    if (!btn) return;
    const maxFiles = this.getMaxFiles(slide);
    const fileList = slide.querySelector('.labellisation-tracker__upload-file-list');
    const count    = fileList?.childElementCount || 0;
    btn.hidden = count >= maxFiles;
  },

  fileNameFromUrl(url) {
    try {
      const urlObj = new URL(url, window.location.href);
      const fParam = urlObj.searchParams.get('f');
      if (fParam) return decodeURIComponent(fParam);
      return decodeURIComponent(urlObj.pathname.split('/').pop());
    } catch { return url; }
  },

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  },
};

// Démarrage : on attend que le DOM soit prêt et que labellisationData soit injecté
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('labellisation-tracker') && typeof labellisationData !== 'undefined') {
    LabellisationTracker.init();
  }
});
